/**
 * @fileoverview Projects the mock Tenant store onto the backend's Active Clients shapes.
 * @description A client is a tenant whose status is `Active Client`. This module turns one into a
 * grid row or into Get Client Details' answer the way the backend does: every client is visible to
 * every Sales role, but a Sales Manager or Executive who does not own the client sees its contacts
 * masked as a unit, only a Super Admin gets the `sales` block, and `allowedActions` lists what the
 * caller may do right now (Invite and Resend for the owner or a Super Admin, Edit, Archive and Restore
 * for a Super Admin only). Never imported outside this feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import {
  hasPendingInvitation,
  hrAdminOf,
  inactivatedAtOf,
  RESEND_COOLDOWN_SECONDS,
  RESEND_LIMIT,
  revisionOf,
} from './active-clients-mock-state'
import {
  MOCK_COUNTRY_CODE,
  referenceFor,
  stateIdByName,
  tierOf,
  toBackendRating,
} from './sales-crm-mock-projection'
import { findSalesUserById } from './sales-roster-mock-store'

import type { MockHrAdmin } from './active-clients-mock-state'
import type {
  ClientAction,
  ClientContact,
  ClientDetails,
  ClientHrAdmin,
  ClientHrAdminState,
  ClientListItem,
  ClientPerson,
  SalesClientStatus,
} from '@/features/sales/types/sales-client.types'
import type { SalesUserRecord, Tenant } from '@/features/sales/types/tenant.types'

/**
 * @description Whether a tenant is a client at all (its bank went Live).
 * @param {Tenant} tenant - The tenant.
 * @returns {boolean} True for an Active Client.
 */
export function isClient(tenant: Tenant): boolean {
  return tenant.status === 'Active Client'
}

/**
 * @description A client's status: archived clients are `INACTIVE`.
 * @param {Tenant} tenant - The client.
 * @returns {SalesClientStatus} Its status.
 */
export function clientStatusOf(tenant: Tenant): SalesClientStatus {
  return inactivatedAtOf(tenant.id) ? 'INACTIVE' : 'ACTIVE'
}

/**
 * @description The state of the bank's first user: an expired or revoked invitation reads as not
 * invited.
 * @param {MockHrAdmin | null} hrAdmin - The HR Admin, if the bank ever had one.
 * @returns {ClientHrAdminState} Their state.
 */
export function hrAdminStateOf(hrAdmin: MockHrAdmin | null): ClientHrAdminState {
  if (!hrAdmin) return 'NOT_INVITED'
  if (hrAdmin.active) return 'ACTIVE'
  return hasPendingInvitation(hrAdmin) ? 'INVITED' : 'NOT_INVITED'
}

/**
 * @description A roster user as a person named on a client.
 * @param {string | null | undefined} userId - The user's roster id.
 * @returns {ClientPerson | null} The person, or null when there is none.
 */
export function personOf(userId: string | null | undefined): ClientPerson | null {
  if (!userId) return null
  return { userId, name: findSalesUserById(userId)?.name ?? userId }
}

/**
 * @description A client's version, as Get Client Details sets it on the `ETag` header.
 * @param {Tenant} tenant - The client.
 * @returns {string} The `tp1.` ETag.
 */
export function clientEtagOf(tenant: Tenant): string {
  return `tp1.${tenant.id}.${String(revisionOf(tenant.id))}`
}

/**
 * @description A client as a grid row.
 * @param {Tenant} tenant - The client.
 * @returns {ClientListItem} The row.
 */
export function toClientListItem(tenant: Tenant): ClientListItem {
  const { code, name } = tierOf(tenant.tier)
  return {
    tenantId: tenant.id,
    tenantCode: tenant.id,
    bankName: tenant.name,
    bankType: referenceFor('bankTypes', tenant.bankType),
    city: tenant.city,
    state: referenceFor('states', tenant.state),
    executive: personOf(tenant.currentOwnerId),
    onboardedAt: tenant.clientSince ?? tenant.createdAt,
    branchCount: tenant.branches,
    tier: { code, name },
    hrAdminState: hrAdminStateOf(hrAdminOf(tenant)),
    status: clientStatusOf(tenant),
  }
}

/**
 * @description Whether the caller owns the client exactly (a downline does not count).
 * @param {Tenant} tenant - The client.
 * @param {SalesUserRecord} caller - The signed-in caller.
 * @returns {boolean} True for the current owner.
 */
export function ownsClient(tenant: Tenant, caller: SalesUserRecord): boolean {
  return tenant.currentOwnerId === caller.id
}

/**
 * @description Whether the caller gets every contact in full: a Super Admin or Sales Head always,
 * anyone else only for a client they own.
 * @param {Tenant} tenant - The client.
 * @param {SalesUserRecord} caller - The signed-in caller.
 * @returns {boolean} True when contacts are not masked.
 */
function seesContacts(tenant: Tenant, caller: SalesUserRecord): boolean {
  return caller.role === 'Super Admin' || caller.role === 'Sales Head' || ownsClient(tenant, caller)
}

/**
 * @description Masks an email address, keeping its first letter and its domain's last part.
 * @param {string} email - The address.
 * @returns {string} E.g. `a****@****.com`.
 */
function maskEmail(email: string): string {
  const [local = '', domain = ''] = email.split('@')
  const suffix = domain.slice(domain.lastIndexOf('.'))
  return `${local.slice(0, 1)}****@****${suffix}`
}

/**
 * @description Masks a phone number, keeping its last four digits.
 * @param {string} phone - The number.
 * @returns {string} E.g. `******3210`.
 */
function maskPhone(phone: string): string {
  return `******${phone.slice(-4)}`
}

/**
 * @description A value in full, or masked, when it is there.
 * @param {string} value - The value, possibly empty.
 * @param {boolean} masked - Whether to mask it.
 * @param {(value: string) => string} mask - How to mask it.
 * @returns {string | null} The value, its mask, or null when it is empty.
 */
function maskedOrNull(value: string, masked: boolean, mask: (value: string) => string) {
  if (!value) return null
  return masked ? mask(value) : value
}

/**
 * @description The bank's primary contact, in full or masked as a unit.
 * @param {Tenant} tenant - The client.
 * @param {boolean} masked - Whether to mask it.
 * @returns {ClientContact} The contact.
 */
function primaryContactOf(tenant: Tenant, masked: boolean): ClientContact {
  const contact = tenant.authorisedContact
  return {
    name: contact.name,
    designation: contact.designation || null,
    email: masked ? maskEmail(contact.email) : contact.email,
    phone: masked ? maskPhone(contact.phone) : `${MOCK_COUNTRY_CODE}${contact.phone}`,
    officePhone: maskedOrNull(contact.officePhone, masked, maskPhone),
    countryCode: masked ? null : MOCK_COUNTRY_CODE,
    mobileNumber: masked ? null : contact.phone,
  }
}

/**
 * @description The HR Admin block of the details, or null when the bank never had one.
 * @param {MockHrAdmin | null} hrAdmin - The HR Admin.
 * @param {boolean} masked - Whether the contacts are masked.
 * @returns {ClientHrAdmin | null} The block.
 */
function hrAdminBlockOf(hrAdmin: MockHrAdmin | null, masked: boolean): ClientHrAdmin | null {
  if (!hrAdmin) return null
  const { invitation } = hrAdmin
  const nextResendMs = invitation
    ? Date.parse(invitation.lastSentAt) + RESEND_COOLDOWN_SECONDS * 1000
    : 0
  return {
    userId: hrAdmin.userId,
    name: hrAdmin.name,
    designation: 'HR Admin',
    email: masked ? maskEmail(hrAdmin.email) : hrAdmin.email,
    phone: masked ? maskPhone(hrAdmin.phone) : `${MOCK_COUNTRY_CODE}${hrAdmin.phone}`,
    userStatus: hrAdmin.active ? 'ACTIVE' : 'INVITED',
    state: hrAdminStateOf(hrAdmin),
    invitedBy: personOf(hrAdmin.invitedById),
    invitedAt: hrAdmin.invitedAt,
    invitationsSent: hrAdmin.invitationsSent,
    invitation: invitation
      ? {
          invitationId: invitation.invitationId,
          status: hasPendingInvitation(hrAdmin) ? 'PENDING' : 'EXPIRED',
          sentAt: invitation.sentAt,
          lastSentAt: invitation.lastSentAt,
          expiresAt: invitation.expiresAt,
          resendCount: invitation.resendCount,
          resendsRemaining: Math.max(0, RESEND_LIMIT - invitation.resendCount),
          nextResendAt: new Date(nextResendMs).toISOString(),
        }
      : null,
  }
}

/**
 * @description What the caller may do with the client right now.
 * @param {Tenant} tenant - The client.
 * @param {SalesUserRecord} caller - The signed-in caller.
 * @returns {ClientAction[]} The allowed actions.
 */
export function allowedActionsOf(tenant: Tenant, caller: SalesUserRecord): ClientAction[] {
  const isSuperAdmin = caller.role === 'Super Admin'
  if (clientStatusOf(tenant) === 'INACTIVE') return isSuperAdmin ? ['RESTORE'] : []
  const actions: ClientAction[] = []
  if (isSuperAdmin || ownsClient(tenant, caller)) {
    const state = hrAdminStateOf(hrAdminOf(tenant))
    if (state === 'NOT_INVITED') actions.push('INVITE_HR_ADMIN')
    if (state === 'INVITED') actions.push('RESEND_HR_ADMIN_INVITE')
  }
  if (isSuperAdmin) actions.push('EDIT', 'DEACTIVATE')
  return actions
}

/**
 * @description A client in full, as Get Client Details answers it for this caller.
 * @param {Tenant} tenant - The client.
 * @param {SalesUserRecord} caller - The signed-in caller.
 * @returns {ClientDetails} The details.
 */
export function toClientDetails(tenant: Tenant, caller: SalesUserRecord): ClientDetails {
  const masked = !seesContacts(tenant, caller)
  const { code, name } = tierOf(tenant.tier)
  return {
    tenantId: tenant.id,
    tenantCode: tenant.id,
    bankName: tenant.name,
    status: clientStatusOf(tenant),
    organisation: {
      bankType: referenceFor('bankTypes', tenant.bankType),
      registrationNumber: tenant.regNumber || null,
      website: tenant.website || null,
      headOfficeLocation: tenant.headOffice,
      streetAddress: tenant.address || null,
      branchCount: tenant.branches,
      employeeCount: tenant.employees,
      tier: { code, name },
      coreBankingSolution: tenant.coreBanking
        ? referenceFor('coreBankingSolutions', tenant.coreBanking)
        : null,
      existingMisSolution: tenant.existingMis || null,
    },
    location: {
      city: tenant.city,
      district: tenant.district || null,
      state: { ...referenceFor('states', tenant.state), id: stateIdByName(tenant.state) },
      pinCode: tenant.pincode || null,
    },
    primaryContact: primaryContactOf(tenant, masked),
    secondaryContact: null,
    sales:
      caller.role === 'Super Admin'
        ? {
            leadSource: referenceFor('leadSources', tenant.leadSource),
            leadRating: toBackendRating(tenant.leadRating),
          }
        : null,
    hrAdmin: hrAdminBlockOf(hrAdminOf(tenant), masked),
    relationship: {
      executive: personOf(tenant.currentOwnerId),
      onboardedAt: tenant.clientSince ?? tenant.createdAt,
    },
    contactsMasked: masked,
    allowedActions: allowedActionsOf(tenant, caller),
  }
}
